import { useState, useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { CIF } from "@/constants/testIds";
import { Package, Truck, ShieldCheck, Calculator, Scale, Wallet } from "lucide-react";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.round(n || 0)));

const fmt2 = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Math.max(0, n || 0));

export const CostBreakdown = () => {
  const [factory, setFactory] = useState(485);
  const [qty, setQty] = useState(26.5);
  const [freight, setFreight] = useState(1850);
  const [insurance, setInsurance] = useState(180);

  const calc = useMemo(() => {
    const goods = factory * qty;
    const cif = goods + freight + insurance;
    const perMt = qty > 0 ? cif / qty : 0;
    return { goods, cif, perMt };
  }, [factory, qty, freight, insurance]);

  // share of bar for stacked viz
  const total = calc.cif || 1;
  const goodsPct = (calc.goods / total) * 100;
  const freightPct = (freight / total) * 100;
  const insurancePct = (insurance / total) * 100;

  const cards = [
    {
      key: "factory",
      icon: Package,
      label: "Factory Price",
      value: fmt(calc.goods),
      sub: `${fmt2(factory)} / MT × ${qty} MT`,
      accent: "bg-slate-900",
      pct: goodsPct,
    },
    {
      key: "freight",
      icon: Truck,
      label: "Estimated Freight",
      value: fmt(freight),
      sub: "Indicative · Container · Lane",
      accent: "bg-blue-600",
      pct: freightPct,
    },
    {
      key: "insurance",
      icon: ShieldCheck,
      label: "Estimated Insurance",
      value: fmt(insurance),
      sub: "Marine cargo coverage",
      accent: "bg-emerald-600",
      pct: insurancePct,
    },
    {
      key: "cif",
      icon: Calculator,
      label: "Estimated CIF",
      value: fmt(calc.cif),
      sub: "Total landed cost",
      featured: true,
    },
    {
      key: "per-mt",
      icon: Scale,
      label: "Cost / MT",
      value: fmt2(calc.perMt),
      sub: "Apples-to-apples comparison",
    },
    {
      key: "order-value",
      icon: Wallet,
      label: "Estimated Total Order Value",
      value: fmt(calc.cif),
      sub: "Use for PO commitment",
    },
  ];

  return (
    <section
      id="breakdown"
      data-testid={CIF.breakdown}
      className="relative py-24 lg:py-32 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-blue-700">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> Cost breakdown
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-medium tracking-[-0.025em] leading-[1.05] text-slate-900 mt-4">
              See exactly where your landed cost comes from.
            </h2>
            <p className="text-slate-500 mt-5 text-[15px] leading-relaxed">
              Move the sliders. Every card recalculates in real time — and the stacked view shows the true mix of your CIF.
            </p>
          </div>

          {/* controls */}
          <div className="lg:w-[420px] rounded-2xl border border-slate-200 bg-slate-50/60 p-5 space-y-5">
            <SliderRow label="Factory Price · USD/MT" min={100} max={1500} step={5} value={factory} setValue={setFactory} format={fmt2} />
            <SliderRow label="Quantity · MT" min={1} max={300} step={0.5} value={qty} setValue={setQty} format={(v) => `${v} MT`} />
            <SliderRow label="Estimated Freight · USD" min={0} max={8000} step={50} value={freight} setValue={setFreight} format={fmt} />
            <SliderRow label="Estimated Insurance · USD" min={0} max={2000} step={10} value={insurance} setValue={setInsurance} format={fmt} />
          </div>
        </div>

        {/* stacked bar */}
        <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50/60 p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-slate-500">CIF composition</div>
            <div className="mono text-[12px] text-slate-700">Total · <span className="font-semibold">{fmt(calc.cif)}</span></div>
          </div>
          <div className="h-3 w-full rounded-full overflow-hidden bg-slate-200 flex">
            <div style={{ width: `${goodsPct}%` }} className="bg-slate-900 transition-[width] duration-500" />
            <div style={{ width: `${freightPct}%` }} className="bg-blue-600 transition-[width] duration-500" />
            <div style={{ width: `${insurancePct}%` }} className="bg-emerald-500 transition-[width] duration-500" />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[12px]">
            <Legend dot="bg-slate-900" label="Factory" pct={goodsPct} />
            <Legend dot="bg-blue-600" label="Freight" pct={freightPct} />
            <Legend dot="bg-emerald-500" label="Insurance" pct={insurancePct} />
          </div>
        </div>

        {/* cards */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((c) => (
            <div
              key={c.key}
              data-testid={CIF.breakdownCard(c.key)}
              className={[
                "group relative rounded-2xl border p-6 transition-all hover:-translate-y-1",
                c.featured
                  ? "bg-slate-950 text-white border-slate-900 shadow-[0_25px_70px_-25px_rgba(15,23,42,0.6)]"
                  : "bg-white border-slate-200 hover:shadow-[0_20px_50px_-25px_rgba(15,23,42,0.18)]",
              ].join(" ")}
            >
              {c.featured && (
                <div className="absolute inset-0 bg-grid-dark opacity-50 rounded-2xl pointer-events-none" />
              )}
              <div className="relative flex items-start justify-between">
                <div className={[
                  "inline-flex h-10 w-10 items-center justify-center rounded-xl",
                  c.featured ? "bg-white/10 text-white" : "bg-slate-900 text-white",
                ].join(" ")}>
                  <c.icon className="h-4 w-4" />
                </div>
                {c.pct !== undefined && (
                  <span className={[
                    "mono text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5 rounded-full",
                    "bg-slate-100 text-slate-600",
                  ].join(" ")}>
                    {c.pct.toFixed(1)}%
                  </span>
                )}
              </div>
              <div className="relative mt-5">
                <div className={[
                  "text-[11px] tracking-widest uppercase font-semibold",
                  c.featured ? "text-blue-300/80" : "text-slate-400",
                ].join(" ")}>{c.label}</div>
                <div className={[
                  "font-display text-3xl font-medium tracking-tight mt-2",
                  c.featured ? "text-white" : "text-slate-900",
                ].join(" ")}>
                  {c.value}
                </div>
                <div className={[
                  "mt-2 text-[12px]",
                  c.featured ? "text-white/55" : "text-slate-500",
                ].join(" ")}>
                  {c.sub}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SliderRow = ({ label, min, max, step, value, setValue, format }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] tracking-wider uppercase font-semibold text-slate-500">{label}</span>
      <span className="mono text-[12px] text-slate-900 font-semibold">{format(value)}</span>
    </div>
    <Slider
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => setValue(v[0])}
      className="[&_[role=slider]]:bg-slate-900 [&_[role=slider]]:border-slate-900"
    />
  </div>
);

const Legend = ({ dot, label, pct }) => (
  <div className="inline-flex items-center gap-2 text-slate-600">
    <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
    {label} <span className="text-slate-400 mono">· {pct.toFixed(1)}%</span>
  </div>
);

export default CostBreakdown;
